import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, InputAdornment, Stack, TextField, Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

import api from '../../../../services/api';
import { useNotify } from '../../../../components/Datatable/NotifyProvider';
import { SUPERFICIE, COR_CATEGORIA } from './estilo';

const ROTA = '/admin/snapshot';

/**
 * Agrupa o catalogo por CONCEITO, nao por linha de comando.
 *
 * No banco, "resumo de BGP" sao quatro registros — um por vendor, com grafias
 * diferentes. Mostrar as quatro linhas soltas obriga o operador a reconhecer
 * que `display bgp peer` e `show ip bgp summary` sao a mesma pergunta, e a
 * marcar as quatro na mao toda vez. Errar uma significa coletar BGP em tres
 * vendors e nao no quarto — e isso so aparece depois, na comparacao, como um
 * equipamento que "nao mudou nada".
 *
 * A chave e o rotulo: e o que `command/free` ja usa para amarrar as grafias.
 */
function agrupar(comandos) {
  const porCategoria = new Map();

  comandos.forEach((c) => {
    const cat = c.category || 'OUTROS';
    if (!porCategoria.has(cat)) porCategoria.set(cat, new Map());
    const conceitos = porCategoria.get(cat);

    // Sem rotulo, o proprio texto vira o conceito — comando solto nao se agrupa
    // com ninguem, e forcar isso juntaria coisas que nao tem relacao.
    const chave = c.label || c.command;
    if (!conceitos.has(chave)) conceitos.set(chave, []);
    conceitos.get(chave).push(c);
  });

  return [...porCategoria.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([categoria, conceitos]) => ({
      categoria,
      conceitos: [...conceitos.entries()]
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([rotulo, itens]) => ({ rotulo, itens })),
    }));
}

/**
 * Criacao de lista de comandos: nome e COMANDOS na mesma tela.
 *
 * Antes so gravava nome e descricao, e nao existia tela nenhuma para vincular
 * comando depois — toda lista nascia vazia e ficava vazia. O efeito no uso e
 * pior que um erro: a coleta roda, nao executa nada, e o run termina como
 * "concluido" com zero saida. Parece que deu certo.
 */
export default function ListaComandosDialog({ aberto, empresa, onFechar, onCriado }) {
  const notify = useNotify();

  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');
  const [catalogo, setCatalogo] = useState([]);
  const [busca, setBusca] = useState('');
  const [selecionados, setSelecionados] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erros, setErros] = useState({});

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const res = await api.get(`${ROTA}/command-set/catalog`, { params: { empresa } });
      setCatalogo(res.data?.data || []);
    } catch {
      setCatalogo([]);
      notify.setNotify({ open: true, severity: 'error', message: 'Erro ao carregar o catálogo' });
    } finally {
      setCarregando(false);
    }
    // notify vem de um Context estável; incluí-lo causaria recarga extra.
  }, [empresa]);

  useEffect(() => {
    if (!aberto) {
      setNome(''); setDescricao(''); setBusca('');
      setSelecionados([]); setErros({});
      return;
    }
    carregar();
  }, [aberto, carregar]);

  const filtrado = useMemo(() => {
    const t = busca.trim().toLowerCase();
    if (!t) return catalogo;
    return catalogo.filter((c) =>
      [c.command, c.label, c.vendor, c.category]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(t)));
  }, [catalogo, busca]);

  const grupos = useMemo(() => agrupar(filtrado), [filtrado]);
  const sel = useMemo(() => new Set(selecionados), [selecionados]);

  const marcar = (ids, ligar) => {
    setErros((e) => ({ ...e, command_ids: null }));
    setSelecionados((atual) => (ligar
      ? [...new Set([...atual, ...ids])]
      : atual.filter((x) => !ids.includes(x))));
  };

  const criar = async () => {
    setSalvando(true);
    setErros({});
    try {
      const res = await api.post(`${ROTA}/command-set/create`, {
        empresa,
        name: nome.trim(),
        description: descricao.trim() || null,
        is_default: false,
        command_ids: selecionados,
      });

      if (res.data?.errors?.length) {
        const porCampo = {};
        res.data.errors.forEach((e) => { porCampo[e.field] = e.msg; });
        setErros(porCampo);
        return;
      }

      const d = res.data.data;
      notify.setNotify({
        open: true,
        severity: 'success',
        message: `Lista “${d.name}” criada com ${d.command_count} comando(s)`,
      });
      onCriado?.();
      onFechar();
    } catch {
      notify.setNotify({ open: true, severity: 'error', message: 'Erro ao criar a lista' });
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Dialog open={aberto} onClose={onFechar} maxWidth="md" fullWidth PaperProps={{ sx: SUPERFICIE }}>
      <DialogTitle sx={{ pb: 1 }}>
        Nova lista de comandos
        <Typography variant="caption" sx={{ display: 'block', color: '#64748b' }}>
          Nome e comandos de uma vez
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 1.5 }}>
          <TextField
            autoFocus size="small" label="Nome da lista" sx={{ flex: 1 }}
            placeholder="Ex.: Janela de borda — OSPF e BGP"
            value={nome}
            onChange={(e) => { setNome(e.target.value); setErros((x) => ({ ...x, name: null })); }}
            error={!!erros.name} helperText={erros.name || ' '}
          />
          <TextField
            size="small" label="Descrição (opcional)" sx={{ flex: 1 }}
            value={descricao} onChange={(e) => setDescricao(e.target.value)}
            helperText=" "
          />
        </Stack>

        <Divider sx={{ mb: 1.5 }} />

        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
          <Typography sx={{ fontWeight: 600, fontSize: 14 }}>Comandos</Typography>
          <TextField
            size="small" placeholder="comando, conceito ou vendor" sx={{ width: 260 }}
            value={busca} onChange={(e) => setBusca(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 17, color: '#94a3b8' }} />
                </InputAdornment>
              ),
            }}
          />
          {carregando && <CircularProgress size={14} />}
          <Box sx={{ flex: 1 }} />
          <Chip
            size="small"
            label={`${selecionados.length} selecionado(s)`}
            sx={{
              height: 21,
              bgcolor: selecionados.length ? '#2563eb' : '#e2e8f0',
              color: selecionados.length ? '#fff' : '#64748b',
            }}
          />
        </Stack>

        {erros.command_ids && <Alert severity="error" sx={{ mb: 1 }}>{erros.command_ids}</Alert>}

        <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 1, height: '42vh', overflow: 'auto' }}>
          {grupos.map((g) => {
            const idsCat = g.conceitos.flatMap((c) => c.itens.map((i) => i.id));
            const todosCat = idsCat.every((i) => sel.has(i));
            const algunsCat = !todosCat && idsCat.some((i) => sel.has(i));

            return (
              <Box key={g.categoria}>
                <Stack
                  direction="row" alignItems="center" spacing={1}
                  sx={{
                    px: 1, py: 0.5, bgcolor: '#f8fafc',
                    position: 'sticky', top: 0, zIndex: 1,
                    borderBottom: '1px solid #e2e8f0',
                  }}
                >
                  <Checkbox
                    size="small" sx={{ p: 0.5 }}
                    checked={todosCat} indeterminate={algunsCat}
                    onChange={(e) => marcar(idsCat, e.target.checked)}
                  />
                  <Chip
                    size="small" label={g.categoria}
                    sx={{
                      height: 18, fontSize: 10.5, fontWeight: 700,
                      bgcolor: COR_CATEGORIA[g.categoria] || COR_CATEGORIA.OUTROS,
                      color: '#fff',
                    }}
                  />
                  <Typography variant="caption" sx={{ color: '#64748b' }}>
                    {idsCat.length} comando(s)
                  </Typography>
                </Stack>

                {g.conceitos.map((c) => {
                  const ids = c.itens.map((i) => i.id);
                  const todos = ids.every((i) => sel.has(i));
                  const alguns = !todos && ids.some((i) => sel.has(i));

                  return (
                    <Stack
                      key={`${g.categoria}-${c.rotulo}`}
                      direction="row" alignItems="center" spacing={1}
                      sx={{ px: 1, py: 0.35, borderBottom: '1px solid #f1f5f9' }}
                    >
                      <Checkbox
                        size="small" sx={{ p: 0.5 }}
                        checked={todos} indeterminate={alguns}
                        onChange={(e) => marcar(ids, e.target.checked)}
                      />
                      <Typography sx={{ fontSize: 13, fontWeight: 600, minWidth: 170 }}>
                        {c.rotulo}
                      </Typography>

                      {/* Cada grafia continua clicavel: nem toda janela mexe nos
                          quatro vendors, e o padrao (marcar o conceito inteiro)
                          nao pode virar obrigacao. */}
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {c.itens.map((i) => (
                          <Tooltip key={i.id} title={i.command} placement="top">
                            <Chip
                              size="small"
                              label={i.vendor}
                              onClick={() => marcar([i.id], !sel.has(i.id))}
                              sx={{
                                height: 20, fontSize: 10.5, cursor: 'pointer',
                                bgcolor: sel.has(i.id) ? '#2563eb' : '#f1f5f9',
                                color: sel.has(i.id) ? '#fff' : '#475569',
                                opacity: i.enabled ? 1 : 0.5,
                              }}
                            />
                          </Tooltip>
                        ))}
                      </Stack>
                    </Stack>
                  );
                })}
              </Box>
            );
          })}

          {!grupos.length && !carregando && (
            <Box sx={{ p: 2 }}>
              <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                {busca
                  ? `Nenhum comando com “${busca}”.`
                  : 'O catálogo está vazio — cadastre um comando antes de montar a lista.'}
              </Typography>
            </Box>
          )}
        </Box>

        <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 1 }}>
          Cada equipamento só executa os comandos do seu próprio vendor — marcar
          os quatro não faz o Huawei rodar comando de Cisco.
        </Typography>
      </DialogContent>

      <DialogActions>
        <Button onClick={onFechar}>Cancelar</Button>
        <Button
          variant="contained"
          disabled={!nome.trim() || !selecionados.length || salvando}
          onClick={criar}
        >
          {salvando ? 'Criando…' : `Criar com ${selecionados.length} comando(s)`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
